import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTeams } from '../use-teams';
import { useSearchParams } from 'react-router-dom';
import { useWorkspaceNavigate } from '../../workspace-path';
import {
  lineItemHasWork,
  lineItemSchema,
  projectSchema,
  taskPrioritySchema,
  taskDependencyEdgeSchema,
  taskSchema,
  taskStatusSchema,
  workspaceMemberSchema,
  type Task,
  type TaskPriority,
  type TaskDependencyEdge,
  type TaskStatus,
  formatSort,
  metafieldSchema,
  type Metafield,
  type SortKey,
} from '@forecastify/shared';
import { Toaster, nextSurface, type Toast } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { TASK_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { bulkTargets, postBulk } from '../use-bulk';
import { applyGridEdit } from './grid-edit';
import { parseList, totalOf, usePermissionSet } from '../permissions-hook';
import { TaskDialog, type TaskDialogLineItem, type TaskDraft } from './task-dialog';
import { usePages } from '../use-pages';
import { useFirstLoad } from '../use-first-load';
import { TasksScreen, type BoardColumnPage, type TaskFilters, type TaskView } from './tasks-screen';
import { setTrackerParams, useTrackerValues } from './tracker-filters';
import { TaskOverlay } from './task-overlay';
import type { TaskMode } from './task-surface';
import { useExport } from '../exports/use-export';
import { useImport } from '../imports/use-import';
import { lineItemContractLabel } from '../contracts/contract-label';
import { requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The Feladatok route: filters to query parameters, and the writes.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The filters go to the SERVER, not to a `.filter()`.** `task.service.ts` builds the WHERE
 * clause, so narrowing to a project narrows the query — which is CLAUDE.md §3's rule and the reason
 * a task list can be unbounded. C7's client and project lists filter in memory on purpose because
 * they are bounded; this one cannot.
 */
export function TasksRoute({ mine = false }: { readonly mine?: boolean } = {}) {
  const session = useSession();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const t = useT();
  const permissions = usePermissionSet();

  /**
   * The task open over this list, and which frame it wears.
   *
   * ⚠️ **`null` closes it.** The mockup keeps the same two variables (`sel` and `taskMode`), and
   * the transitions between frames are the feature — a drawer that can only be closed rather than
   * grown into a modal or a page is three screens wearing a costume.
   */
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  /**
   * ⚠️ **Seeded to the ladder's answer for a list**, so a task opened before any handler runs — a
   * deep link that lands here — matches what a click would have produced.
   */
  const [mode, setMode] = useState<TaskMode>(nextSurface('page'));

  const [tasks, setTasks] = useState<readonly Task[]>([]);

  /**
   * ⚠️ **`?open=<key>&mode=<frame>` — how the full page shrinks back to an overlay.** The task
   * page cannot render a drawer over itself, so it navigates here and says what it wanted; this
   * turns that into state and clears the address, or Back would re-open it for ever.
   */
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const wanted = params.get('open');
    if (wanted === null) return;
    const task = tasks.find((candidate) => candidate.key === wanted);
    /* ⚠️ Wait for the list: on a cold load the rows arrive after this effect first runs, and
     * clearing the parameter now would throw the intent away before it could be honoured. */
    if (task === undefined) return;
    setOpenTaskId(task.id);
    setMode(params.get('mode') === 'modal' ? 'modal' : 'drawer');
    const next = new URLSearchParams(params);
    next.delete('open');
    next.delete('mode');
    setParams(next, { replace: true });
  }, [params, setParams, tasks]);

  /**
   * ⚠️⚠️ **`?new=1` OPENS THE CREATE DIALOG - item 71, and the convention is already the
   * product's.** `delivery-route.tsx` has read exactly this parameter since the sidebar gained its
   * `+`: creating something is a DIALOG on a list screen, and a query parameter is how the shell
   * asks for one without knowing anything about that screen's internals. The command palette is
   * the second caller, and it needed no new mechanism.
   *
   * ⚠️ **Cleared with `replace`, or the parameter re-opens the dialog on every Back** - and
   * puts it back on screen after somebody cancelled it. Same note, same reason, as the sibling
   * effect above.
   */
  useEffect(() => {
    if (params.get('new') === null) return;
    setDialogOpen(true);
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
  }, [params, setParams]);
  const [statuses, setStatuses] = useState<readonly TaskStatus[]>([]);
  /* ⚠️ The board's columns, for `reload` — a ref, so the statuses arriving do not re-run it. */
  const statusesRef = useRef<readonly TaskStatus[]>([]);
  /**
   * ⚠️ 31 Oct 2026 · **the board's own pages, one per status column**, zero-based like `usePages`.
   * A column missing from the map is on its first page. Each column's filtered total comes back
   * with its page.
   */
  const [columnIndex, setColumnIndex] = useState<Readonly<Record<string, number>>>({});
  const [columnTotals, setColumnTotals] = useState<Readonly<Record<string, number>>>({});
  const [priorities, setPriorities] = useState<readonly TaskPriority[]>([]);
  const [projects, setProjects] = useState<readonly { id: string; name: string }[]>([]);
  /** ⚠️ C25 · every line item in the workspace; the dialog narrows by the chosen project. */
  const [lineItems, setLineItems] = useState<readonly TaskDialogLineItem[]>([]);
  const [dependencies, setDependencies] = useState<readonly TaskDependencyEdge[]>([]);
  const [members, setMembers] = useState<readonly { id: string; name: string }[]>([]);
  /**
   * The filters, seeded ONCE from the address.
   *
   * The projects list links here with `?projectId=<id>` (*Feladatok szűrése erre*), and this route
   * kept its filters purely in local state — so the parameter reached the request URL through
   * nothing and the link landed on an unfiltered list. Seeded in the initialiser rather than
   * synced by an effect, because after the first render the filter bar is the reader's: an effect
   * would keep pulling them back to the project the link named every time they cleared it.
   */
  /**
   * ⚠️ **`mine` PINS THE ASSIGNEE AND HIDES THE CONTROL FOR IT.** *Feladataim* is this screen
   * with one filter already answered — not a second screen — so it is the same route, the same
   * request and the same four views. What it must NOT be is a filter somebody can clear, which is
   * why the toolbar drops that chip rather than pre-selecting it.
   *
   * ⚠️ The member id comes from the SESSION's workspace row, not from a "me" lookup: `/auth/me`
   * already answered it when the workspace was chosen, and a second source could disagree.
   */
  const myMemberId =
    session.workspaces.find((workspace) => workspace.id === session.workspaceId)?.memberId ?? '';

  /* R-60 · the assignee's team, as a filter. */
  const teams = useTeams();
  const [filters, setFilters] = useState<TaskFilters>(() => ({
    search: '',
    projectId: params.get('projectId') ?? '',
    statusId: '',
    priorityId: '',
    assigneeMemberId: '',
    teamId: '',
    /* ⚠️ Item 16's three. Not seeded from the address: only `?projectId=` is a link anybody
       follows, and a deadline range in a URL would need an encoding somebody could mistype. */
    dueRange: '',
    estimateRange: '',
    metafields: {},
    component: '',
    fixVersion: '',
    epic: '',
  }));
  /** Same, for the view switch — `?view=timeline` is the row menu's *Idővonal*. */
  const [initialView] = useState<TaskView | undefined>(() => {
    const wanted = params.get('view');
    return wanted === 'list' || wanted === 'board' || wanted === 'timeline' || wanted === 'grid'
      ? wanted
      : undefined;
  });
  /**
   * ⚠️ **The route tracks the view because the views page differently** — see `TasksScreen`'s
   * `onViewChange`. Seeded from the same `?view=` the screen is, so a bookmarked board does not
   * spend its first load on a page it will not use.
   */
  const [view, setView] = useState<TaskView>(initialView ?? 'list');
  const pages = usePages();
  const { settle: settlePage } = pages;
  /**
   * ⚠️ **Sorting resets the page, and the reason differs from a filter's**: re-sorting does not
   * change WHICH tasks match, so page four still exists — it now holds different tasks, and
   * staying on it lands somewhere nobody asked to be.
   *
   * ⚠️ **The board and the timeline ignore it entirely.** Neither is a list, and a Gantt sorted by
   * priority is a Gantt with its rows shuffled — the sort travels only when `view === 'list'`.
   */
  const [sort, setSort] = useState<readonly SortKey[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  /* ⚠️ T-10's shared paths. `useImport` reloads the list, because rows landed behind the dialog. */
  const exporter = useExport();
  const [dialogOpen, setDialogOpen] = useState(false);
  /**
   * Item 27 · the workspace's `task` fields.
   *
   * ⚠️ **Loaded once and NOT fatal**, like the clients route's own metafield feed: a list that
   * refused to render because a column definition did not arrive would be trading every task for
   * an optional column.
   */
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/metafields?entity=task').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(metafieldSchema, await response.json());
      if (parsed !== null) setMetafields(parsed);
    })();
  }, [session]);

  /**
   * ⚠⚠️ **THE FILTERS THE REQUEST USES, WHICH ON *Feladataim* ARE NOT THE ONES IN STATE.**
   * The first version pinned the assignee only on the way to the SCREEN, so the toolbar looked
   * right and the query went out unfiltered — 28 rows, every one of them somebody else's. The
   * request and the toolbar have to read the same object.
   */
  const effective = useMemo(
    () => (mine ? { ...filters, assigneeMemberId: myMemberId } : filters),
    [filters, mine, myMemberId],
  );
  /* J-1 · the three tracker filters' values, narrowed to the project filter, re-read with the tasks. */
  const trackerValues = useTrackerValues(effective.projectId, tasks);

  /**
   * ⚠️ **Every filter becomes a query parameter, and an EMPTY one is omitted.** `?projectId=` would
   * filter for a project whose id is the empty string and return nothing — which reads on screen as
   * *"this project has no tasks"* rather than as a bug in the request.
   *
   * ⚠️ **The filters only** — every view's request starts from these and adds its own paging.
   */
  const filterQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (effective.search.trim() !== '') params.set('search', effective.search.trim());
    if (effective.projectId !== '') params.set('projectId', effective.projectId);
    if (effective.statusId !== '') params.set('statusId', effective.statusId);
    if (effective.priorityId !== '') params.set('priorityId', effective.priorityId);
    if (effective.teamId !== '') params.set('teamId', effective.teamId);
    if (effective.assigneeMemberId !== '') {
      params.set('assigneeMemberId', effective.assigneeMemberId);
    }
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ ITEM 16 · WHERE `from..to` BECOMES TWO PARAMETERS, AND THE ONLY PLACE.│
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * `list-toolbar.tsx` argues the one-string encoding: everything between the control and here
     * treats a filter's value as opaque, which is what kept the active test, the chip and *clear
     * all* free of a second branch. This is the boundary where it stops being opaque.
     *
     * ⚠️ **Each end is written only when it is set**, so `8..` sends `estimateMin` alone rather
     * than an empty `estimateMax` the server would have to decide the meaning of.
     */
    const range = (value: string, lower: string, upper: string): void => {
      if (value === '') return;
      const [from = '', to = ''] = value.split('..');
      if (from !== '') params.set(lower, from);
      if (to !== '') params.set(upper, to);
    };
    range(effective.dueRange, 'dueAfter', 'dueBefore');
    range(effective.estimateRange, 'estimateMin', 'estimateMax');
    setTrackerParams(params, effective);

    /* ⚠️ **`?mf.<key>=` for a choice and `?mf.<key>.from=` / `.to=` for a range** — the server has
       understood the first since item 35 and the second since item 16. A value containing `..` is
       a range, which is unambiguous: the ranged types are `number` and `date`, and neither can
       hold a literal `..`. */
    for (const [key, value] of Object.entries(effective.metafields)) {
      if (value === '') continue;
      if (value.includes('..')) range(value, `mf.${key}.from`, `mf.${key}.to`);
      else params.set(`mf.${key}`, value);
    }
    return params.toString();
  }, [effective]);

  /**
   * ⚠️⚠️ **EVERY VIEW PAGES ON THE SERVER — 31 Oct 2026.** The board and the timeline used to read
   * every page of the filtered set, because a board distributing 200 of 400 tasks into columns is
   * wrong rather than partial. On UAT that set is 29,591 tasks, so *every page* stopped at the
   * 5,000 ceiling and was wrong anyway. Now the list, the grid and the timeline ask for one page of
   * the filtered set, and the board asks for one page per column (`reload`), each column counting
   * its own total — so no column is ever silently short.
   */
  const query = useMemo(() => {
    const params = new URLSearchParams(filterQuery);
    /* ⚠️ An OFFSET since item 20 — `use-pages.ts` records why the cursor stack went. */
    if (pages.offset > 0) params.set('offset', String(pages.offset));
    if (view === 'list' || view === 'grid') {
      /* ⚠️ `due_on:asc,priority:desc` — the ORDER is the priority, and the server maps each key
         through `TASK_SORT`. Item 45, D3: the sort is the server's, never a page reordered here.
         ⚠️ The list's and the grid's only: a Gantt sorted by priority has its rows shuffled. */
      const encoded = formatSort(sort);
      if (encoded !== undefined) params.set('sort', encoded);
    }
    /* ⚠️ The timeline asks only for what it can draw. Its page used to fill with a tracker's undated
       issues, leaving the Gantt empty. */
    if (view === 'timeline') params.set('scheduled', 'true');
    const text = params.toString();
    return text === '' ? '' : `?${text}`;
    /* ⚠️ `pages.offset` and `view` are inputs to the query, so they belong here — leaving them out
       builds the string once and pages a URL that never changes. */
  }, [filterQuery, view, sort, pages.offset]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    if (view === 'board') {
      if (!(await readBoard())) return;
    } else {
      const response = await session.request(`/tasks${query}`).catch(() => null);
      if (response === null) {
        setFailure({ kind: 'error', code: 'unavailable' });
        return;
      }
      if (!response.ok) {
        setFailure(await failureOf(response));
        return;
      }

      const body: unknown = await response.json();
      const parsed = parseList(taskSchema, body);
      if (parsed === null) {
        setFailure({ kind: 'error', code: 'validation_failed' });
        return;
      }

      setFailure(null);
      setTasks(parsed);
      settlePage(totalOf(body));
    }

    /**
     * The timeline's arrows.
     *
     * ⚠️ **Read HERE rather than with the pickers, because a write changes it.** Adding a
     * dependency in the drawer has to move the arrow on the chart behind it, and the pickers load
     * once per mount.
     *
     * ⚠️ **NOT FATAL, and not part of the guard above.** A caller whose edge read fails should get
     * their task list with no arrows on it, not an error page instead of their work.
     */
    const edgeResponse = await session.request('/tasks/dependencies').catch(() => null);
    if (edgeResponse?.ok === true) {
      const edges = parseList(taskDependencyEdgeSchema, await edgeResponse.json());
      if (edges !== null) setDependencies(edges);
    }
    /**
     * ⚠️⚠️ **`settlePage`, NOT `pages`, AND THE DIFFERENCE IS AN INFINITE LOOP.** `pages` is
     * memoised over its own state, so `settle` changing it would change `pages`, which would change
     * `reload`, which would run and settle again. `settle` is React's own `useState` setter and is
     * stable for the component's life — depending on it says what this callback actually uses.
     */

    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE BOARD: ONE PAGE PER STATUS COLUMN, EACH WITH ITS OWN TOTAL.      │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️ **A column is the filters plus its status**, so the header counts what that column holds
     * under the filters and the pager under it reaches the rest. With a status filter set, that one
     * column is the only one asked for — the others hold nothing under it, and say so.
     *
     * ⚠️ **All or nothing, like the list.** A column that fails fails the board: a board with one
     * column quietly empty is the partial answer this replaced.
     *
     * ⚠️ **A page past the end is pulled back.** A move or a colleague's change can empty a
     * column's last page; staying on it would show an empty column with cards behind it.
     */
    async function readBoard(): Promise<boolean> {
      let columns = statusesRef.current;
      if (columns.length === 0) {
        /* The picker feeds have not answered yet on a cold load of `?view=board`. */
        const response = await session.request('/task-statuses').catch(() => null);
        const list =
          response?.ok === true ? parseList(taskStatusSchema, await response.json()) : null;
        if (list === null) {
          setFailure({ kind: 'error', code: 'unavailable' });
          return false;
        }
        columns = list;
      }
      const ids =
        effective.statusId === '' ? columns.map((status) => status.id) : [effective.statusId];
      const responses = await Promise.all(
        ids.map((id) => {
          const params = new URLSearchParams(filterQuery);
          params.set('statusId', id);
          params.set('limit', String(BOARD_COLUMN_PAGE));
          const offset = (columnIndex[id] ?? 0) * BOARD_COLUMN_PAGE;
          if (offset > 0) params.set('offset', String(offset));
          return session.request(`/tasks?${params.toString()}`).catch(() => null);
        }),
      );

      const read: { id: string; rows: Task[]; total: number }[] = [];
      for (const [at, response] of responses.entries()) {
        if (response === null) {
          setFailure({ kind: 'error', code: 'unavailable' });
          return false;
        }
        if (!response.ok) {
          setFailure(await failureOf(response));
          return false;
        }
        const body: unknown = await response.json();
        const rows = parseList(taskSchema, body);
        if (rows === null) {
          setFailure({ kind: 'error', code: 'validation_failed' });
          return false;
        }
        read.push({ id: ids[at] ?? '', rows, total: totalOf(body) });
      }

      setFailure(null);
      setTasks(read.flatMap((column) => column.rows));
      setColumnTotals(Object.fromEntries(read.map((column) => [column.id, column.total])));
      const past = read.filter(
        (column) => (columnIndex[column.id] ?? 0) > lastPageOf(column.total),
      );
      if (past.length > 0) {
        setColumnIndex((current) => ({
          ...current,
          ...Object.fromEntries(past.map((column) => [column.id, lastPageOf(column.total)])),
        }));
      }
      return true;
    }
  }, [session, query, view, settlePage, filterQuery, effective.statusId, columnIndex]);

  /** Each board column's total and pager, for the screen. */
  const boardColumns = useMemo((): Readonly<Record<string, BoardColumnPage>> | undefined => {
    if (view !== 'board') return undefined;
    return Object.fromEntries(
      Object.entries(columnTotals).map(([id, total]): [string, BoardColumnPage] => {
        const last = lastPageOf(total);
        const index = Math.min(columnIndex[id] ?? 0, last);
        const goTo = (to: number): void => {
          setColumnIndex((current) => ({ ...current, [id]: Math.min(Math.max(0, to), last) }));
        };
        return [
          id,
          {
            total,
            page: {
              index,
              count: last + 1,
              total,
              hasPrevious: index > 0,
              hasNext: index < last,
              onPrevious: () => {
                goTo(index - 1);
              },
              onNext: () => {
                goTo(index + 1);
              },
              onGoTo: goTo,
            },
          },
        ];
      }),
    );
  }, [view, columnTotals, columnIndex]);

  /* ⚠️ Declared after `reload`, because the import hook takes it: rows landed behind the dialog, so
   * the list underneath is stale the moment the report appears. */
  const importer = useImport(() => void reload());

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. A colleague's change
   * refetches this in place — no spinner, no blank, no reload. R-19. */
  useLiveInvalidation(TASK_TOPICS, reload);

  /**
   * ⚠️ **The four picker feeds load ONCE, not on every filter change.** They do not depend on the
   * filter, and refetching four lists on every keystroke in the search box would make typing feel
   * like the network. Their failure is not fatal — rule 5 answers `not_found` to somebody without
   * `people.view`, and the screen should still list tasks.
   */
  useEffect(() => {
    const alive = { current: true };

    void (async () => {
      if (session.workspaceId === null) return;

      const [statusList, priorityList, projectList, memberList, itemList] = await Promise.all([
        session.request('/task-statuses').catch(() => null),
        session.request('/task-priorities').catch(() => null),
        requestAll(session, '/projects').catch(() => null),
        requestAll(session, '/members').catch(() => null),
        /* ⚠️ C25 · **non-fatal like the four above.** A tenant without contracts is refused, which
           lands as an empty list — and empty is exactly how the dialog is told to draw no picker. */
        session.request('/line-items').catch(() => null),
      ]);

      if (!alive.current) return;

      if (statusList?.ok === true) {
        const list = parseList(taskStatusSchema, await statusList.json());
        if (list !== null) {
          statusesRef.current = list;
          setStatuses(list);
        }
      }
      if (priorityList?.ok === true) {
        const list = parseList(taskPrioritySchema, await priorityList.json());
        if (list !== null) setPriorities(list);
      }
      if (projectList?.ok === true) {
        const list = parseList(projectSchema, await projectList.json());
        if (list !== null) {
          setProjects(list.map((project) => ({ id: project.id, name: project.name })));
        }
      }
      if (memberList?.ok === true) {
        const list = parseList(workspaceMemberSchema, await memberList.json());
        if (list !== null) {
          setMembers(list.map((member) => ({ id: member.id, name: member.displayName })));
        }
      }
      if (itemList?.ok === true) {
        const list = parseList(lineItemSchema, await itemList.json());
        /* ⚠️ The WHOLE workspace's, unfiltered: the dialog narrows them by whichever project the
           person picks, which is a choice that only exists inside it. */
        if (list !== null) {
          setLineItems(
            /**
             * ⚠️⚠️ **C56 · A LINE ITEM WITH NO PROJECT CANNOT HOLD A TASK, SO IT IS NOT
             * OFFERED.** That is the whole reason a project-less contract may carry only
             * licences: a task belongs to a project and an hour to a task. Filtering here keeps
             * `TaskDialogLineItem` honest — both its fields stay non-null because every item
             * that survives this really has them.
             */
            /* ⚠️ U-7 · and a licence takes no work at all — the server refuses a task on one
               with `line_item_takes_no_work`, so offering it would be offering a 409. */
            list.flatMap((item) =>
              item.projectId === null || !lineItemHasWork(item.type)
                ? []
                : [
                    {
                      id: item.id,
                      name: item.name,
                      contractCode: lineItemContractLabel(item),
                      projectId: item.projectId,
                      /* IP-3 · a closed one is drawn and unpickable — `line_item_closed`. */
                      closed: item.status === 'closed',
                    },
                  ],
            ),
          );
        }
      }
    })();

    return () => {
      alive.current = false;
    };
  }, [session]);

  /**
   * ⚠️ **The id is a counter, not `Date.now()`.** Two toasts fired in the same millisecond — a
   * move and its refusal, which is exactly the pair that happens — would share a key, and React
   * would render one of them.
   */
  const toast = (entry: Omit<Toast, 'id'>): void => {
    setToasts((current) => [...current, { ...entry, id: nextToastId() }]);
  };

  const write = async (path: string, init: RequestInit, onDone?: () => void): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const envelope = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { message?: string }[] };
      } | null;
      const code = envelope?.error?.code ?? 'unavailable';
      const detail = envelope?.error?.details?.[0]?.message ?? '';

      /* Branching on `code`, never on `message` — rule 5. */
      if (code === 'assignee_not_a_project_member') {
        setServerError(t('error.assignee_not_a_project_member', { name: detail }));
      } else if (code === 'line_item_takes_no_work') {
        /* U-7 · a licence line item takes no task. */
        setServerError(t('error.line_item_takes_no_work'));
      } else if (code === 'contract_closed') {
        setServerError(t('error.contract_closed', { name: detail }));
      } else if (code === 'line_item_closed') {
        /* IP-3 · nor does a closed one, from inside the product. */
        setServerError(t('error.line_item_closed', { name: detail }));
      } else if (code === 'task_status_unmapped') {
        /* J-2 · the tracker's status has no counterpart here — a board drag lands on this. */
        setServerError(t('error.task_status_unmapped', { status: detail }));
      } else if (code === 'task_tag_not_in_tracker') {
        /* J-6 · a tag the tracker has no label for, on a synced task. */
        setServerError(t('error.task_tag_not_in_tracker', { tag: detail }));
      } else if (code === 'conflict') setServerError(t('error.conflict'));
      else if (code === 'validation_failed') setServerError(t('task.error.estimate'));
      else if (code === 'not_found') setServerError(t('error.not_found'));
      else setServerError(t('error.unavailable'));
      return false;
    }
    onDone?.();
    await reload();
    return true;
  };

  /** One `PATCH` per change, which is what the endpoint is shaped for. */
  /**
   * ⚠️ **One place for all four bulk actions**, so the *"what landed"* accounting and the reload
   * cannot drift between *change status* and *delete*.
   */
  const runBulk = async (
    path: string,
    ids: readonly string[],
    body: Record<string, unknown>,
    key: 'tasks.toast.bulkEdited' | 'tasks.toast.bulkDeleted',
  ): Promise<void> => {
    const result = await postBulk(session, path, { ...body, targets: bulkTargets(tasks, ids) });
    await reload();
    if (result === null) {
      toast({ title: t('error.unavailable'), variant: 'error' });
      return;
    }
    toast({
      title: t(key, { count: result.done.length }),
      ...(result.failed.length === 0
        ? {}
        : { description: t('bulk.someRefused', { total: ids.length }) }),
      variant: result.failed.length === 0 ? 'success' : 'error',
    });
  };

  const patch = async (task: Task, body: Record<string, unknown>): Promise<boolean> =>
    write(`/tasks/${task.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...body, version: task.version }),
    });

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <TasksScreen
          loading={loading}
          tasks={tasks}
          trackerValues={trackerValues}
          statuses={statuses}
          priorities={priorities}
          projects={projects}
          members={members}
          teams={teams}
          metafields={metafields}
          filters={effective}
          onFiltersChange={(next) => {
            /* ⚠️⚠️ **A FILTER CHANGE RESETS THE PAGE, AND FORGETTING THIS SHOWS AN EMPTY LIST.** The
               cursor is a position in the ordered set the OLD filter produced; in the new one it
               almost always lands past the end, and an empty page reads as *"no tasks match"*. */
            pages.reset();
            setColumnIndex({});
            setFilters(next);
          }}
          sort={sort}
          onSortChange={(next) => {
            pages.reset();
            setSort(next);
          }}
          /**
           * ⚠️⚠️ **ITEM 72 · ONE REQUEST, AND THE ANSWER IS PER ROW.** A selection is made against
           * a page loaded minutes ago, so a task somebody else has moved since is the ordinary case
           * rather than the edge — the version travels with each id and a stale row comes back in
           * `failed` rather than being overwritten (`bulk.ts`).
           *
           * ⚠️ **The toast counts what LANDED**, not what was selected. Saying *"12 updated"* after
           * nine went through is the lie `state-rules.md` §5 is about.
           */
          {...(permissions.has('task.update')
            ? {
                onBulkEdit: (ids: readonly string[], patch: Record<string, unknown>) => {
                  void runBulk('/tasks/bulk', ids, patch, 'tasks.toast.bulkEdited');
                },
              }
            : {})}
          {...(permissions.has('task.delete')
            ? {
                onBulkDelete: (ids: readonly string[]) => {
                  void runBulk('/tasks/bulk-delete', ids, {}, 'tasks.toast.bulkDeleted');
                },
              }
            : {})}
          mine={mine}
          dependencies={dependencies}
          initialView={initialView}
          onViewChange={(next) => {
            /* ⚠️ Back to the first page on every switch. A cursor is a position in the LIST's
               ordered set; carrying it into a board that then asks for 200 would page a request
               that is not paging, and coming back would resume somewhere nobody chose. */
            setView(next);
            pages.reset();
            setColumnIndex({});
          }}
          {...(boardColumns === undefined ? {} : { boardColumns })}
          page={
            /* ⚠️ **The list, the grid and the timeline** — one page of the filtered set each. The
               board pages per column instead (`boardColumns`). */
            view === 'board'
              ? undefined
              : {
                  hasPrevious: pages.hasPrevious,
                  hasNext: pages.hasNext,
                  onPrevious: pages.previous,
                  onNext: pages.next,
                  /* ⚠️ Item 20's `[<] [selector] [>] [total]`. The total counts what the filters
                     left, which is what `list.pageTotal` says in words. */
                  index: pages.index,
                  count: pages.count,
                  total: pages.total,
                  onGoTo: pages.goTo,
                }
          }
          canCreate={permissions.has('task.create')}
          canEdit={permissions.has('task.update')}
          serverError={serverError}
          onCreate={() => {
            setDialogOpen(true);
          }}
          /**
           * ⚠⚠️ **A ROW OPENS THE DRAWER, IT DOES NOT NAVIGATE — AND THIS IS THE HALF OF THE
           * TRI-MODAL DETAIL THAT WAS MISSING.** C8 built all three frames (`task-surface.tsx`:
           * drawer, modal, full page) and `main.jsx` opens every task the same way:
           * `openTask(k) { setState({ sel: k, taskMode: 'drawer' }) }`. Ours went straight to
           * `/tasks/:key`, so the only frame anybody ever saw was the full page and the other two
           * were unreachable — built, working and inert, which is the failure this project keeps
           * finding.
           *
           * ⚠️ **The list stays mounted underneath**, which is the point of a drawer: a triage pass
           * over twenty tasks is twenty peeks, not twenty round trips through a route.
           */
          onOpen={(task) => {
            setOpenTaskId(task.id);
            /* ⚠️ **Item 34 · a drill-in from a LIST opens as a MODAL.** Every one of these
               hardcoded `'drawer'` and every list therefore taught the same one surface;
               `nextSurface` in `@forecastify/ui` carries the ladder. */
            setMode(nextSurface('page'));
          }}
          onMoveStatus={(task, statusId) => {
            void moveStatus(task, statusId);
          }}
          onReschedule={(task, startsOn, dueOn) => {
            void (async () => {
              const ok = await patch(task, { startsOn, dueOn });
              if (ok) {
                toast({
                  title: t('tasks.rescheduled', { key: task.key, from: startsOn, to: dueOn }),
                  variant: 'success',
                });
              }
            })();
          }}
          onExport={() => {
            exporter.open('task');
          }}
          onImport={() => {
            importer.open('task');
          }}
          onGridEdit={(task, column, value) => {
            void gridEdit(task, column, value);
          }}
        />
      </ScreenState>

      {/**
       * ⚠⚠️ **THE DRAWER AND THE MODAL, OVER THE LIST.** `task-surface.tsx` already drew all
       * three frames; nothing opened two of them. This is the caller that does.
       *
       * ⚠️ **`full` LEAVES for the route**, because a full page is an address — R-8's rule, and what
       * makes a task linkable at all. The other two are overlays and stay here, so the list
       * underneath keeps its scroll position, its filters and its selection.
       */}
      <TaskOverlay
        taskId={openTaskId}
        mode={mode}
        canEdit={permissions.has('task.update')}
        canLogTime={permissions.has('worklog.create')}
        canEditWorklog={permissions.has('worklog.update')}
        canSchedule={permissions.has('task.schedule')}
        onModeChange={(next) => {
          if (next === 'full') {
            const task = tasks.find((candidate) => candidate.id === openTaskId);
            setOpenTaskId(null);
            if (task !== undefined) go(`/tasks/${task.key}`);
            return;
          }
          setMode(next);
        }}
        onClose={() => {
          setOpenTaskId(null);
        }}
        onChanged={() => {
          void reload();
        }}
        onOpenTask={(id) => {
          setOpenTaskId(id);
        }}
      />

      <TaskDialog
        open={dialogOpen}
        statuses={statuses}
        priorities={priorities}
        /**
         * ⚠️⚠️ **EVERYBODY, AND SINCE THIS DIALOG CAN NOW CHOOSE A PROJECT THAT IS A REAL
         * TRADE.** The note this replaces said the constraint did not apply because there was no
         * project to check against — true then, and no longer. P-1 makes membership the right to be
         * assigned, so picking a project and then a non-member earns a named refusal from the
         * server (`assignee_not_a_project_member`) with the dialog left open. Narrowing the list
         * here would mean fetching that project's membership on every selection, for a rule the
         * server states clearly; the project's own tab already passes its members instead.
         */
        assignees={members}
        projects={projects}
        lineItems={lineItems}
        serverError={serverError}
        onClose={() => {
          setDialogOpen(false);
        }}
        onSubmit={(draft: TaskDraft) => {
          void write(
            '/tasks',
            {
              method: 'POST',
              body: JSON.stringify({
                title: draft.title.trim(),
                description: draft.description.trim() === '' ? null : draft.description,
                /* ⚠️ P2-32.2 · the line item decides the project; the project rides along as a
                   consistency check, and neither may be null any more. */
                ...(draft.projectId === '' ? {} : { projectId: draft.projectId }),
                lineItemId: draft.lineItemId,
                statusId: draft.statusId === '' ? undefined : draft.statusId,
                priorityId: draft.priorityId === '' ? undefined : draft.priorityId,
                assigneeMemberId: draft.assigneeMemberId === '' ? null : draft.assigneeMemberId,
                ...(draft.estimateHours.trim() === ''
                  ? {}
                  : { estimateHours: draft.estimateHours.trim() }),
                startsOn: draft.startsOn === '' ? null : draft.startsOn,
                dueOn: draft.dueOn === '' ? null : draft.dueOn,
              }),
            },
            () => {
              setDialogOpen(false);
            },
          );
        }}
      />

      {exporter.dialog}
      {importer.dialog}

      {/* ⚠️ One `Toaster`, three sources — two in the same corner hide each other. */}
      <Toaster
        toasts={[...toasts, ...exporter.toasts, ...importer.toasts]}
        label={t('tasks.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
          exporter.dismiss(id);
          importer.dismiss(id);
        }}
      />
    </>
  );

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ A BOARD MOVE, AND ITS UNDO.                                          │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️ **A status holds as many tasks as it is given** — the product owner's ruling, 14 Oct 2026:
   * there is no WIP limit, so a move is never refused here for the size of the column.
   *
   * ⚠️ **Undo is a real reverse write, not a local rollback.** It sends the task back to the status
   * it came from, which is why the previous status is captured before the move. A rollback that only
   * changed local state would leave the server holding the new value.
   */
  async function moveStatus(task: Task, statusId: string): Promise<void> {
    const target = statuses.find((status) => status.id === statusId);
    if (target === undefined) return;

    const from = task.statusId;
    const ok = await patch(task, { statusId });
    if (!ok) return;

    toast({
      title: t('tasks.moved', { key: task.key, name: target.name }),
      variant: 'success',
      action: {
        label: t('tasks.moved.undo'),
        failureLabel: t('tasks.moved.undoFailed'),
        /**
         * ⚠️⚠️ **THE PROMISE IS RETURNED, AND `Toaster` REQUIRES IT FOR A REASON.** Its own
         * note: *"undo is a reverse mutation and can itself fail"*, and returning `void` makes a
         * failed undo indistinguishable from a successful one — which is the mockup's behaviour,
         * because it undoes an in-memory array and cannot fail.
         *
         * So this **throws** when the reverse write does not land, and the toast shows
         * `failureLabel` instead of quietly pretending.
         *
         * ⚠️ **The task is RE-READ first.** Its `version` moved on when the move succeeded; using
         * the captured one would be a stale write, and the undo would 409 for a reason nobody can
         * see from the screen.
         */
        onAction: async () => {
          const fresh = await session.request(`/tasks/${task.id}`).catch(() => null);
          if (fresh?.ok !== true) throw new Error('the task could not be re-read');

          const parsed = taskSchema.safeParse(await fresh.json());
          if (!parsed.success) throw new Error('the task did not match its own schema');

          const undone = await patch(parsed.data, { statusId: from });
          if (!undone) throw new Error('the reverse write was refused');
        },
      },
    });
  }

  /* ⚠️ `grid-edit.ts` does the work — item 30 gave the project's own tab the same grid, and a
     second copy of this resolution is the drift `task-columns.ts` exists to prevent. */
  async function gridEdit(task: Task, column: string, value: string): Promise<void> {
    await applyGridEdit(task, column, value, {
      statuses,
      members,
      patch,
      onUnknown: setServerError,
      t,
    });
  }
}

/**
 * How many cards one board column shows per page — the API's default page, asked for by name so a
 * change to that default does not quietly resize the board.
 */
const BOARD_COLUMN_PAGE = 50;

/** A column's last page, zero-based — never below the first, even when the column is empty. */
function lastPageOf(total: number): number {
  return Math.max(0, Math.ceil(total / BOARD_COLUMN_PAGE) - 1);
}

/** A monotonic toast id. Module-level, so remounting the route does not reuse one. */
let toastCounter = 0;
function nextToastId(): number {
  toastCounter += 1;
  return toastCounter;
}
